import { useEffect, useMemo, useReducer, useState } from "react"
import { ActionIcon, Badge, Button, Flex, Group, Modal, MultiSelect, Select, Stack, Tabs, Text, TextInput, Tooltip } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { useCreateDoctor, useDeleteDoctor, useDoctorMasters, useDoctors, useUpdateDoctor } from "@features/doctor/hooks/use-doctors.ts"
import { useClinics } from "@features/clinic/hooks/use-clinics.ts"
import { IconCalendar, IconClock, IconEdit, IconPlus, IconSchool, IconSearch, IconStethoscope, IconTrash, IconX } from "@tabler/icons-react"
import { PAGE_SIZES, ROLES } from "@/shared"
import _ from "lodash"
import dayjs from "dayjs"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import { headerStore } from "@shared/state/header.state.ts"
import { openConfirmModal } from "@mantine/modals"
import { Doctor } from "../domain/doctor.types"

interface SearchState {
  nombre: string
  especialidades: string
  clinica: string
  correo: string
  telefono: string
  dni: string
  cmp: string
}

interface State {
  search: SearchState
  sortStatus: DataTableSortStatus<Doctor>
  page: number
  pageSize: number
}

type Action = { type: "SET_SEARCH"; payload: Partial<SearchState> } | { type: "SET_SORT_STATUS"; payload: DataTableSortStatus<Doctor> } | { type: "SET_PAGE"; payload: number } | { type: "SET_PAGE_SIZE"; payload: number }

const initialState: State = {
  search: {
    nombre: "",
    especialidades: "",
    clinica: "",
    correo: "",
    telefono: "",
    dni: "",
    cmp: ""
  },
  sortStatus: { columnAccessor: "nombre", direction: "asc" },
  page: 1,
  pageSize: PAGE_SIZES[1]
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_SORT_STATUS":
      return { ...state, sortStatus: action.payload }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload, page: 1 }
    default:
      return state
  }
}

export default function DoctorsPage() {
  const { hasRole } = useAuth()
  const { tableHeight } = useDynamicHeight()
  const { data: doctors, isLoading: fetching, refetch } = useDoctors()
  const { data: clinicsData } = useClinics()
  const createDoctorMutation = useCreateDoctor()
  const updateDoctorMutation = useUpdateDoctor()
  const deleteDoctorMutation = useDeleteDoctor()

  const emptyDoctor: Partial<Doctor> = {
    nombre: "",
    especialidades: [],
    clinica_id: "",
    clinica_ids: [],
    clinicaId: "",
    correo: "",
    telefono: "",
    dni: "",
    cmp: "",
    cumpleanos: "",
    horario_atencion: { especificos: [], recurrentes: [] },
    segmentos: [],
    articulaciones: [],
    competencias: [],
    intereses: [],
    hobbies: [],
    preferencia_instrumentistas: [],
    secretaria_nombre: "",
    secretaria_telefono: "",
    secretaria_cumple: "",
    secretaria_correo: ""
  }

  const [opened, setOpened] = useState(false)
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null)
  const [newDoctor, setNewDoctor] = useState<Partial<Doctor>>(emptyDoctor)

  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    headerStore.setTitle("Gestión de Médicos")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const handleCreate = async () => {
    // Validaciones básicas
    if (!newDoctor.nombre) return
    if (newDoctor.dni && !/^\d{8}$/.test(newDoctor.dni)) {
      // Podría mostrar notificación aquí
    }
    if (newDoctor.cmp && !/^\d{6}$/.test(newDoctor.cmp)) {
      // Podría mostrar notificación aquí
    }

    if (editingDoctor) {
      await updateDoctorMutation.mutateAsync({ id: editingDoctor.id, doctorData: newDoctor })
    } else {
      await createDoctorMutation.mutateAsync(newDoctor)
    }
    setOpened(false)
    setEditingDoctor(null)
    setNewDoctor(emptyDoctor)
  }

  const handleEdit = (doctor: Doctor) => {
    setEditingDoctor(doctor)
    setNewDoctor({
      ...doctor,
      especialidades: doctor.especialidades || [],
      clinica_ids: doctor.clinica_ids || [],
      segmentos: doctor.segmentos || [],
      articulaciones: doctor.articulaciones || [],
      competencias: doctor.competencias || [],
      intereses: doctor.intereses || [],
      hobbies: doctor.hobbies || [],
      preferencia_instrumentistas: doctor.preferencia_instrumentistas || [],
      horario_atencion: {
        especificos: (doctor.horario_atencion?.especificos || []).map((e: any) => (typeof e === "string" ? { fecha: e, inicio: "08:00", fin: "17:00" } : e)),
        recurrentes: doctor.horario_atencion?.recurrentes || []
      }
    })
    setOpened(true)
  }

  const handleDelete = async (id: string) => {
    openConfirmModal({
      title: "Eliminar médico",
      centered: true,
      children: <Text size="sm">¿Está seguro de eliminar este médico? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red", loading: deleteDoctorMutation.isPending, leftSection: <IconTrash size={15} /> },
      onConfirm: async () => {
        await deleteDoctorMutation.mutateAsync(id)
      }
    })
  }

  const filteredRecords = useMemo(() => {
    if (!doctors) return []
    let result = [...doctors]

    const { nombre, especialidades, clinica, correo, telefono, dni, cmp } = state.search

    if (nombre) result = result.filter((r) => r.nombre?.toLowerCase().includes(nombre.toLowerCase()))
    if (especialidades) result = result.filter((r) => r.especialidades?.join(", ").toLowerCase().includes(especialidades.toLowerCase()))
    if (clinica) result = result.filter((r) => r.clinicaId?.toLowerCase().includes(clinica.toLowerCase()) || r.clinicas?.nombre?.toLowerCase().includes(clinica.toLowerCase()))
    if (correo) result = result.filter((r) => r.correo?.toLowerCase().includes(correo.toLowerCase()))
    if (telefono) result = result.filter((r) => r.telefono?.toLowerCase().includes(telefono.toLowerCase()))
    if (dni) result = result.filter((r) => r.dni?.toLowerCase().includes(dni.toLowerCase()))
    if (cmp) result = result.filter((r) => r.cmp?.toLowerCase().includes(cmp.toLowerCase()))

    result = _.orderBy(result, [state.sortStatus.columnAccessor], [state.sortStatus.direction])

    return result
  }, [doctors, state.search, state.sortStatus])

  const pagedRecords = useMemo(() => {
    const from = (state.page - 1) * state.pageSize
    const to = from + state.pageSize
    return filteredRecords.slice(from, to)
  }, [filteredRecords, state.page, state.pageSize])

  const { data: masters } = useDoctorMasters()

  const getOptions = (key: string, field: keyof Doctor) => {
    const fromMasters = masters?.[key]?.map((m: any) => m.nombre || m.id) || []
    const fromDoctors = doctors?.flatMap((d: any) => d[field] || []).filter(Boolean) || []
    return _.uniq([...fromMasters, ...fromDoctors]).map((val) => ({ value: val, label: val }))
  }

  const clearSearch = (field: keyof SearchState) => {
    dispatch({ type: "SET_SEARCH", payload: { [field]: "" } })
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end" align="center">
        {(hasRole(ROLES.ADMIN) || hasRole(ROLES.DOCTOR_CREATE)) && (
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              setEditingDoctor(null)
              setNewDoctor(emptyDoctor)
              setOpened(true)
            }}
          >
            Agregar Médico
          </Button>
        )}
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={fetching}
        records={pagedRecords}
        totalRecords={filteredRecords.length}
        recordsPerPage={state.pageSize}
        page={state.page}
        onPageChange={(p) => dispatch({ type: "SET_PAGE", payload: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(s) => dispatch({ type: "SET_PAGE_SIZE", payload: s })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(s) => dispatch({ type: "SET_SORT_STATUS", payload: s })}
        columns={[
          {
            accessor: "nombre",
            title: "Nombre",
            sortable: true,
            filtering: state.search.nombre !== "",
            filter: (
              <TextInput
                placeholder="Buscar por nombre"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.nombre && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("nombre")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.nombre}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { nombre: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "dni",
            title: "DNI",
            sortable: true,
            filtering: state.search.dni !== "",
            filter: (
              <TextInput
                placeholder="Buscar DNI"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.dni && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("dni")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.dni}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { dni: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "cmp",
            title: "CMP",
            sortable: true,
            filtering: state.search.cmp !== "",
            filter: (
              <TextInput
                placeholder="Buscar CMP"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.cmp && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("cmp")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.cmp}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { cmp: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "especialidades",
            title: "Especialidades",
            sortable: true,
            filtering: state.search.especialidades !== "",
            filter: (
              <TextInput
                placeholder="Buscar especialidades"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.especialidades && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("especialidades")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.especialidades}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { especialidades: e.currentTarget.value } })}
              />
            ),
            render: (record: Doctor) => (
              <Group gap={4}>
                {record.especialidades?.map((e: string) => (
                  <Badge key={e} variant="light">
                    {e}
                  </Badge>
                ))}
              </Group>
            )
          },
          {
            accessor: "clinicaId",
            title: "Clínica/Hospital",
            sortable: true,
            filtering: state.search.clinica !== "",
            filter: (
              <TextInput
                placeholder="Buscar clínica"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.clinica && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("clinica")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.clinica}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { clinica: e.currentTarget.value } })}
              />
            ),
            render: (record: Doctor) => {
              const names = record.medico_clinicas?.map((mc: any) => mc.clinicas?.nombre).filter(Boolean) || []
              if (names.length > 0) return names.join(", ")
              return record.clinicaId || record.clinicas?.nombre || "-"
            }
          },
          {
            accessor: "correo",
            title: "Email",
            sortable: true,
            filtering: state.search.correo !== "",
            filter: (
              <TextInput
                placeholder="Buscar correo"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.correo && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("correo")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.correo}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { correo: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "telefono",
            title: "Teléfono",
            sortable: true,
            filtering: state.search.telefono !== "",
            filter: (
              <TextInput
                placeholder="Buscar teléfono"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.telefono && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("telefono")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.telefono}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { telefono: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !hasRole(ROLES.ADMIN) && !hasRole(ROLES.DOCTOR_EDIT) && !hasRole(ROLES.DOCTOR_DELETE),
            render: (doctor: Doctor) => (
              <Group gap={4} justify="right" wrap="nowrap">
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.DOCTOR_EDIT)) && (
                  <Tooltip label="Editar Médico">
                    <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => handleEdit(doctor)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.DOCTOR_DELETE)) && (
                  <Tooltip label="Eliminar Médico">
                    <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDelete(doctor.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
        style={{ height: tableHeight }}
        noRecordsText="No se encontraron médicos"
      />

      <Modal opened={opened} onClose={() => setOpened(false)} title={editingDoctor ? "Editar Médico" : "Agregar Nuevo Médico"} centered size="xl">
        <Tabs defaultValue="basicos">
          <Tabs.List>
            <Tabs.Tab value="basicos">Datos Básicos</Tabs.Tab>
            <Tabs.Tab value="perfil">Perfil y Segmentación</Tabs.Tab>
            <Tabs.Tab value="horarios">Horarios</Tabs.Tab>
            <Tabs.Tab value="secretaria">Secretaría</Tabs.Tab>
            {editingDoctor && (
              <Tabs.Tab value="visitas" leftSection={<IconStethoscope size={16} />}>
                Visitas
              </Tabs.Tab>
            )}
            {editingDoctor && (
              <Tabs.Tab value="talleres" leftSection={<IconSchool size={16} />}>
                Talleres
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="basicos" pt="md">
            <Stack>
              <TextInput label="Nombre completo" placeholder="Nombre completo" value={newDoctor.nombre} onChange={(e) => setNewDoctor({ ...newDoctor, nombre: e.target.value })} required />
              <Group grow>
                <TextInput label="DNI" placeholder="8 dígitos" value={newDoctor.dni} onChange={(e) => setNewDoctor({ ...newDoctor, dni: e.target.value.replace(/\D/g, "").slice(0, 8) })} />
                <TextInput label="CMP" placeholder="6 dígitos" value={newDoctor.cmp} onChange={(e) => setNewDoctor({ ...newDoctor, cmp: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
              </Group>
              <Group grow>
                <TextInput label="Email" placeholder="Correo electrónico" value={newDoctor.correo} onChange={(e) => setNewDoctor({ ...newDoctor, correo: e.target.value })} />
                <TextInput label="Teléfono" placeholder="Teléfono" value={newDoctor.telefono} onChange={(e) => setNewDoctor({ ...newDoctor, telefono: e.target.value })} />
              </Group>
              <DatePickerInput label="Cumpleaños" placeholder="Mes y año" value={newDoctor.cumpleanos ? dayjs(newDoctor.cumpleanos).toDate() : null} onChange={(date) => setNewDoctor({ ...newDoctor, cumpleanos: date ? dayjs(date).format("YYYY-MM-DD") : "" })} valueFormat="MMMM YYYY" />
              <MultiSelect label="Clínicas" placeholder="Seleccionar clínicas" data={clinicsData?.map((c: any) => ({ value: c.id, label: c.nombre })) || []} value={newDoctor.clinica_ids} onChange={(vals) => setNewDoctor({ ...newDoctor, clinica_ids: vals })} searchable clearable />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="perfil" pt="md">
            <Stack>
              <MultiSelect label="Especialidades" placeholder="Seleccionar especialidades" data={getOptions("especialidades", "especialidades")} value={newDoctor.especialidades} onChange={(v) => setNewDoctor({ ...newDoctor, especialidades: v })} searchable />
              <MultiSelect label="Segmentos" placeholder="Seleccionar segmentos" data={getOptions("segmentos", "segmentos")} value={newDoctor.segmentos} onChange={(v) => setNewDoctor({ ...newDoctor, segmentos: v })} searchable />
              <MultiSelect label="Articulaciones" placeholder="Seleccionar articulaciones" data={getOptions("articulaciones", "articulaciones")} value={newDoctor.articulaciones} onChange={(v) => setNewDoctor({ ...newDoctor, articulaciones: v })} searchable />
              <MultiSelect label="Competencias" placeholder="Seleccionar competencias" data={getOptions("competencias", "competencias")} value={newDoctor.competencias} onChange={(v) => setNewDoctor({ ...newDoctor, competencias: v })} searchable />
              <MultiSelect label="Intereses" placeholder="Seleccionar intereses" data={getOptions("intereses", "intereses")} value={newDoctor.intereses} onChange={(v) => setNewDoctor({ ...newDoctor, intereses: v })} searchable />
              <MultiSelect label="Hobbies" placeholder="Seleccionar hobbies" data={getOptions("hobbies", "hobbies")} value={newDoctor.hobbies} onChange={(v) => setNewDoctor({ ...newDoctor, hobbies: v })} searchable />
              <MultiSelect
                label="Preferencias de instrumentistas"
                placeholder="Seleccionar preferencias"
                data={getOptions("preferencia_instrumentistas", "preferencia_instrumentistas")}
                value={newDoctor.preferencia_instrumentistas}
                onChange={(v) => setNewDoctor({ ...newDoctor, preferencia_instrumentistas: v })}
                searchable
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="horarios" pt="md">
            <Stack>
              <DatePickerInput
                type="multiple"
                label="Fechas específicas de atención"
                placeholder="Seleccione fechas"
                value={
                  newDoctor.horario_atencion?.especificos
                    ?.map((esp: any) => {
                      const dateStr = typeof esp === "string" ? esp : esp.fecha
                      return dayjs(dateStr).toDate()
                    })
                    .filter((d: any) => !isNaN(d.getTime())) || []
                }
                onChange={(dates) => {
                  const currentEspecificos = newDoctor.horario_atencion?.especificos || []
                  const newEspecificos = dates.map((d) => {
                    const dateStr = dayjs(d).format("YYYY-MM-DD")
                    const existing = currentEspecificos.find((e: any) => (typeof e === "string" ? e === dateStr : e.fecha === dateStr))
                    if (existing) {
                      return typeof existing === "string" ? { fecha: existing, inicio: "08:00", fin: "17:00" } : existing
                    }
                    return { fecha: dateStr, inicio: "08:00", fin: "17:00" }
                  })
                  setNewDoctor({
                    ...newDoctor,
                    horario_atencion: { ...newDoctor.horario_atencion, especificos: newEspecificos }
                  })
                }}
                leftSection={<IconCalendar size={18} />}
                clearable
              />
              {newDoctor.horario_atencion?.especificos?.length > 0 && (
                <Text size="sm" fw={500} mt="xs">
                  Horarios para fechas específicas
                </Text>
              )}
              {newDoctor.horario_atencion?.especificos?.map((esp: any, index: number) => (
                <Group key={index} grow>
                  <TextInput label="Fecha" value={esp.fecha} readOnly variant="filled" />
                  <TextInput
                    label="Inicio"
                    type="time"
                    value={esp.inicio}
                    onChange={(e) => {
                      const newEsp = [...newDoctor.horario_atencion.especificos]
                      newEsp[index].inicio = e.target.value
                      setNewDoctor({ ...newDoctor, horario_atencion: { ...newDoctor.horario_atencion, especificos: newEsp } })
                    }}
                  />
                  <TextInput
                    label="Fin"
                    type="time"
                    value={esp.fin}
                    onChange={(e) => {
                      const newEsp = [...newDoctor.horario_atencion.especificos]
                      newEsp[index].fin = e.target.value
                      setNewDoctor({ ...newDoctor, horario_atencion: { ...newDoctor.horario_atencion, especificos: newEsp } })
                    }}
                  />
                </Group>
              ))}
              <Text size="sm" fw={500}>
                Horarios Recurrentes
              </Text>
              <Button
                variant="light"
                leftSection={<IconClock size={16} />}
                onClick={() => {
                  const currentRec = newDoctor.horario_atencion?.recurrentes || []
                  setNewDoctor({
                    ...newDoctor,
                    horario_atencion: {
                      ...newDoctor.horario_atencion,
                      recurrentes: [...currentRec, { dias: "L-V", inicio: "08:00", fin: "17:00" }]
                    }
                  })
                }}
              >
                Añadir horario recurrente
              </Button>
              {newDoctor.horario_atencion?.recurrentes?.map((rec: any, index: number) => {
                const recurringOptions = ["L-V", "Fines de semana"]
                const selectedDays = newDoctor.horario_atencion.recurrentes.map((r: any) => r.dias)
                const availableOptions = recurringOptions.filter((opt) => opt === rec.dias || !selectedDays.includes(opt))

                return (
                  <Group key={index} grow>
                    <Select
                      label="Días"
                      placeholder="Seleccionar días"
                      data={availableOptions}
                      value={rec.dias}
                      onChange={(v) => {
                        const newRec = [...newDoctor.horario_atencion.recurrentes]
                        newRec[index].dias = v || ""
                        setNewDoctor({ ...newDoctor, horario_atencion: { ...newDoctor.horario_atencion, recurrentes: newRec } })
                      }}
                    />
                    <TextInput
                      label="Inicio"
                      type="time"
                      placeholder="08:00"
                      value={rec.inicio}
                      onChange={(e) => {
                        const newRec = [...newDoctor.horario_atencion.recurrentes]
                        newRec[index].inicio = e.target.value
                        setNewDoctor({ ...newDoctor, horario_atencion: { ...newDoctor.horario_atencion, recurrentes: newRec } })
                      }}
                    />
                    <TextInput
                      label="Fin"
                      type="time"
                      placeholder="17:00"
                      value={rec.fin}
                      onChange={(e) => {
                        const newRec = [...newDoctor.horario_atencion.recurrentes]
                        newRec[index].fin = e.target.value
                        setNewDoctor({ ...newDoctor, horario_atencion: { ...newDoctor.horario_atencion, recurrentes: newRec } })
                      }}
                    />
                    <ActionIcon
                      color="red.7"
                      variant="subtle"
                      mt={24}
                      onClick={() => {
                        const newRec = newDoctor.horario_atencion.recurrentes.filter((_: any, i: number) => i !== index)
                        setNewDoctor({ ...newDoctor, horario_atencion: { ...newDoctor.horario_atencion, recurrentes: newRec } })
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                )
              })}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="secretaria" pt="md">
            <Stack>
              <TextInput label="Nombre de la secretaria" placeholder="Nombre de la secretaria" value={newDoctor.secretaria_nombre} onChange={(e) => setNewDoctor({ ...newDoctor, secretaria_nombre: e.target.value })} />
              <TextInput label="Teléfono de la secretaria" placeholder="Teléfono de la secretaria" value={newDoctor.secretaria_telefono} onChange={(e) => setNewDoctor({ ...newDoctor, secretaria_telefono: e.target.value })} />
              <TextInput label="Correo de la secretaria" placeholder="Correo de la secretaria" value={newDoctor.secretaria_correo} onChange={(e) => setNewDoctor({ ...newDoctor, secretaria_correo: e.target.value })} />
              <DatePickerInput
                label="Cumpleaños de la secretaria"
                placeholder="Mes y año"
                value={newDoctor.secretaria_cumple ? dayjs(newDoctor.secretaria_cumple).toDate() : null}
                onChange={(date) => setNewDoctor({ ...newDoctor, secretaria_cumple: date ? dayjs(date).format("YYYY-MM-DD") : "" })}
                valueFormat="MMMM YYYY"
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Button fullWidth mt="xl" onClick={handleCreate} loading={createDoctorMutation.isPending || updateDoctorMutation.isPending}>
          Guardar Médico
        </Button>
      </Modal>
    </Stack>
  )
}
